import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { GeldProvider } from "./context/GeldContext"; // Import the GeldProvider
import ProtectedRoute from "./components/ProtectedRoute";
import LoadingSpinner from "./components/LoadingSpinner";
import Login from "./components/Login";
import Register from "./components/Register";
import MathApp from "./MathApp";
import Dashboard from "./components/Dashboard";
import LandingPage from "./components/LandingPage";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { Analytics } from "@vercel/analytics/react"; // Add this import

import "./App.css";

// Wrapper component to handle auth-aware redirects
const AuthRedirect = ({ children, redirectTo }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  // If user is already authenticated, redirect to dashboard
  if (user) return <Navigate to={redirectTo} />;

  // Otherwise, render the login/register page
  return children;
};

// Conditional Home route component
const HomeRoute = () => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  // If user is authenticated, show dashboard. Otherwise, show landing page.
  return user ? <Dashboard /> : <LandingPage />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Root path - shows Dashboard for logged in users, Landing page for others */}
      <Route path="/" element={<HomeRoute />} />

      {/* Auth Routes with redirects for already logged-in users */}
      <Route
        path="/login"
        element={
          <AuthRedirect redirectTo="/dashboard">
            <Login />
          </AuthRedirect>
        }
      />
      <Route
        path="/register"
        element={
          <AuthRedirect redirectTo="/dashboard">
            <Register />
          </AuthRedirect>
        }
      />

      {/* Legal pages */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Dashboard - protected */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Other protected routes */}
      <Route
        path="/learn"
        element={
          <ProtectedRoute>
            <MathApp mode="learn" />
          </ProtectedRoute>
        }
      />

      <Route
        path="/practice"
        element={
          <ProtectedRoute>
            <MathApp mode="practice" />
          </ProtectedRoute>
        }
      />

      {/* No longer need /try route since landing page is the trial */}
      {/* Redirect any other route to home */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <GeldProvider>
        <Router>
          <AppRoutes />
          <Analytics /> {/* Add the Analytics component here */}
        </Router>
      </GeldProvider>
    </AuthProvider>
  );
}

export default App;
